import React from 'react';
import { bool, number, oneOf, shape, string } from 'prop-types';
import AmpPinterest from 'components/AmpPinterest';
import AmpPrismEntryButton from 'components/AmpPrismEntryButton';
import { imageUrl } from 'util/images';
import { generateWidthList } from 'util/images/responsiveImages';
import { getAffiliateLinkProps } from 'util/affiliates/amazon';
import ImageCaption from 'components/ImageCaption';
import styles from './styles';

// --------- responsive IMG data -------------

// used for img width prop and for building srcset
const MAX_POST_IMG_WIDTH = 549;

// used for building srcset
export const DEFAULT_MAX_SIZE = {
  width: MAX_POST_IMG_WIDTH,
  aspectRatio: 4 / 5
};

// --------- End responsive IMG data -------------

const DEFAULT_ALT = 'Post Image';

function AmpImg(props) {
  const {
    alt,
    caption,
    credit,
    isPinnable,
    layout,
    maxSize,
    originalHeight,
    originalWidth,
    placement,
    post: { canonical },
    url,
    href,
    isAffiliate,
    hashedPublicId,
    hasPrism
  } = props;

  const imgAlt = alt || DEFAULT_ALT; // alt cannot be null

  // We want to minimize markup, so we cap out at the maxSize for generating
  // srcSet urls
  const width = maxSize.width || originalWidth;

  // For aspectRatio we defer to the original dimensions though
  const aspectRatio =
    originalWidth > 0 && originalHeight > 0
      ? originalWidth / originalHeight
      : maxSize.aspectRatio;

  // We want to maintain aspect ratio with the new width
  const height = aspectRatio ? width / aspectRatio : null;

  const imgProps = {
    src:
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
    srcSet: generateWidthList(
      url,
      { resizeMode: 'crop', width },
      width,
      aspectRatio
    ),
    width,
    height,
    alt: imgAlt,
    layout
  };

  const showCredit = (caption || credit) && placement !== 'interrupter';
  const showPrism = hasPrism && hashedPublicId;

  return (
    <div className="AmpImg">
      {href ? (
        <a
          className="AmpImg__link"
          {...(isAffiliate ? getAffiliateLinkProps(href) : { href })}
        >
          <amp-img {...imgProps} />
        </a>
      ) : (
        <amp-img {...imgProps} />
      )}

      {isPinnable && canonical && (
        <div className="AmpImg__pinterest">
          <AmpPinterest
            media={imageUrl(url, { width })}
            url={canonical}
            description={imgAlt}
          />
        </div>
      )}
      {(showCredit || showPrism) && (
        <div className="AmpImg__info">
          {showCredit && <ImageCaption credit={credit} caption={caption} />}
          {showPrism && <AmpPrismEntryButton imageId={hashedPublicId} />}
        </div>
      )}
      <style jsx>{styles}</style>
    </div>
  );
}

AmpImg.propTypes = {
  isFixedSize: bool,
  alt: string,
  caption: string,
  credit: string,
  isPinnable: bool,
  layout: string,
  maxSize: shape({
    width: number,
    aspectRatio: number
  }),
  originalHeight: number,
  originalWidth: number,
  placement: oneOf(['cover', 'inPost', 'interrupter', 'sideBySide']),
  post: shape({ canonical: string }),
  url: string.isRequired,
  href: string,
  isAffiliate: bool,
  hashedPublicId: string,
  hasPrism: bool
};

AmpImg.defaultProps = {
  isFixedSize: false,
  alt: DEFAULT_ALT,
  caption: undefined,
  credit: undefined,
  originalHeight: 687,
  originalWidth: MAX_POST_IMG_WIDTH,
  isPinnable: true,
  layout: 'responsive',
  maxSize: DEFAULT_MAX_SIZE,
  placement: 'inPost',
  post: {
    canonical: undefined
  },
  href: undefined,
  isAffiliate: false,
  hashedPublicId: undefined,
  hasPrism: false
};

export default AmpImg;
